<template>
  <CModal
    title="Invoice Preview"
    color="info"
    size="lg"
    :show.sync="showModal"
  >
    <CFormGroup class="form-group form-row mb-3">
      <template #label>
        <label class="col-form-label col-sm-3">
          Date Range
        </label>
      </template>

      <template #input>
        <date-range-picker
          v-model="dateRange"
          opens="right"
          class="col-sm-9 col-12"
          :locale-data="{ firstDay: 1, format: 'mediumDate' }"
          :min-date="minDate"
          :max-date="maxDate"
          @update="handleDateRangeUpdate"
        >
          <template #input>
            <span v-if="startDate && endDate">
              {{ formatDate(startDate) }} - {{ formatDate(endDate) }}
            </span>
            <span
              v-else
              class="text-muted"
            >
              Select date range (optional)
            </span>
          </template>
        </date-range-picker>
      </template>
    </CFormGroup>

    <hr>

    <div v-if="loading">
      <CSpinner color="info" />
      Loading invoice preview...
    </div>

    <div v-else-if="hasPreviewData">
      <div class="mb-3">
        <strong>Period:</strong> {{ formatDate(previewData.startDate) }} - {{ formatDate(previewData.endDate) }}
      </div>

      <table class="table table-sm table-bordered">
        <thead>
          <tr>
            <th>Category</th>
            <th>Description</th>
            <th class="text-right">
              Total Calls
            </th>
            <th class="text-right">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!previewData.lineItems.length">
            <td
              colspan="4"
              class="text-center text-muted"
            >
              No billable calls
            </td>
          </tr>
          <template v-for="(item, idx) in previewData.lineItems">
            <!-- Main line item row -->
            <tr :key="`item-${idx}`">
              <td>
                <CButton
                  v-if="hasPostbackBreakdown(item)"
                  size="sm"
                  color="light"
                  variant="ghost"
                  class="mr-2 p-1"
                  @click="togglePostbackBreakdown(idx)"
                >
                  <CIcon
                    :name="expandedLineItems[idx] ? 'cil-chevron-bottom' : 'cil-chevron-right'"
                    size="sm"
                    class="text-primary"
                  />
                </CButton>
                {{ item.category }}
              </td>
              <td>{{ item.description }}</td>
              <td class="text-right">
                {{ item.totalCalls.toLocaleString() }}
              </td>
              <td class="text-right">
                {{ formatAmount(item.amount) }}
              </td>
            </tr>

            <!-- Postback breakdown row (shown when expanded) -->
            <tr
              v-if="hasPostbackBreakdown(item) && expandedLineItems[idx]"
              :key="`breakdown-${idx}`"
              class="postback-breakdown-row"
            >
              <td colspan="4">
                <PostbackCallsBreakdown :calls="item.postbackCallsBreakdown" />
              </td>
            </tr>
          </template>
          <tr
            v-if="previewData.adjustmentAmount !== 0"
            class="table-secondary"
          >
            <td
              colspan="3"
              class="text-right"
            >
              Adjustments:
            </td>
            <td class="text-right">
              {{ formatAmount(previewData.adjustmentAmount) }}
            </td>
          </tr>
          <tr
            v-if="previewData.disputeFeeAmount !== 0"
            class="table-secondary"
          >
            <td
              colspan="3"
              class="text-right"
            >
              Dispute Fees:
            </td>
            <td class="text-right">
              {{ formatAmount(previewData.disputeFeeAmount) }}
            </td>
          </tr>
          <tr class="font-weight-bold">
            <td
              colspan="3"
              class="text-right"
            >
              Total:
            </td>
            <td class="text-right">
              {{ formatAmount(total) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else>
      <p class="alert alert-info">
        <CIcon name="cil-info" />
        There were no calls in the selected period. Try selecting different dates.
      </p>
    </div>

    <template #footer>
      <CButton
        color="secondary"
        @click="closeModal"
      >
        Close
      </CButton>
    </template>
  </CModal>
</template>

<script>
import moment from 'moment'
import { formatAmount, formatDate } from '@/utils'
import { mapActions } from 'vuex'
import PostbackCallsBreakdown from '@/components/PostbackCallsBreakdown.vue'

export default {
  name: 'InvoicePreviewModal',

  components: {
    PostbackCallsBreakdown
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    accountId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      response: null,
      startDate: null,
      endDate: null,
      minDate: moment().subtract(1, 'year').startOf('day').toDate(),
      maxDate: moment().endOf('day').toDate(),
      expandedLineItems: {}
    }
  },

  computed: {
    showModal: {
      get() {
        return this.show
      },
      set(value) {
        if (!value) {
          this.$emit('update:show', false)
        }
      }
    },

    dateRange: {
      get() {
        return {
          startDate: this.startDate,
          endDate: this.endDate
        }
      },
      set({ startDate, endDate }) {
        this.startDate = moment(startDate).startOf('day').toDate()
        this.endDate = moment(endDate).endOf('day').toDate()
      }
    },

    previewData() {
      if (!this.response) {
        return null
      }

      return {
        lineItems: this.response.lineItems || [],
        adjustmentAmount: this.response.adjustmentAmount || 0,
        disputeFeeAmount: this.response.disputeFeeAmount || 0,
        startDate: this.response.startDate,
        endDate: this.response.endDate
      }
    },

    hasPreviewData() {
      return this.previewData && (
        this.previewData.lineItems.length ||
        this.previewData.adjustmentAmount !== 0 ||
        this.previewData.disputeFeeAmount !== 0
      )
    },

    total() {
      if (!this.response || !this.response.lineItems) {
        return 0
      }

      const lineItemsTotal = this.response.lineItems.reduce((sum, item) => sum + (item.isDisplayOnly ? 0 : item.amount), 0)
      return lineItemsTotal -
             (this.response.adjustmentAmount || 0) +
             (this.response.disputeFeeAmount || 0)
    },

    hasPostbackBreakdown() {
      return (lineItem) => {
        return lineItem?.postbackCallsBreakdown &&
               Array.isArray(lineItem.postbackCallsBreakdown) &&
               lineItem.postbackCallsBreakdown.length > 0
      }
    }
  },

  watch: {
    show: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          // Reset date range to ensure clean modal on each open
          this.startDate = null
          this.endDate = null
          this.expandedLineItems = {}
          this.loadPreview()
        }
      }
    }
  },

  methods: {
    ...mapActions('billing', [
      'previewInvoice'
    ]),

    async loadPreview() {
      this.loading = true
      this.response = null
      this.expandedLineItems = {}

      try {
        const response = await this.previewInvoice({
          accountId: this.accountId,
          startDate: this.startDate ? moment(this.startDate).startOf('day').format('YYYY-MM-DD') : null,
          endDate: this.endDate ? moment(this.endDate).add(1, 'day').startOf('day').format('YYYY-MM-DD') : null
        })

        this.response = response

        // Update the date picker to show the dates from the response if not already set
        if (response.startDate && response.endDate) {
          if (!this.startDate || !this.endDate) {
            this.startDate = moment(response.startDate).toDate()
            this.endDate = moment(response.endDate).toDate()
          }
        }
      } catch (error) {
        this.$notify({
          title: 'Error',
          text: 'Failed to load invoice preview',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    handleDateRangeUpdate() {
      this.loadPreview()
    },

    closeModal() {
      this.$emit('update:show', false)
    },

    togglePostbackBreakdown(idx) {
      this.$set(this.expandedLineItems, idx, !this.expandedLineItems[idx])
    },

    formatAmount,
    formatDate
  }
}
</script>

<style scoped>
.postback-breakdown-row {
  background-color: #f8f9fa;
}
</style>
